import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Search,
  Building2,
  Users,
  Trophy,
  Medal,
  Award,
  ChevronRight,
  Loader2,
  Mail,
  Phone,
  ExternalLink
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { cn } from "../lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_STYLES = {
  Gold: { bg: "bg-yellow-100", text: "text-yellow-800", border: "border-yellow-200" },
  Silver: { bg: "bg-slate-100", text: "text-slate-700", border: "border-slate-200" },
  Bronze: { bg: "bg-orange-100", text: "text-orange-800", border: "border-orange-200" },
  Unranked: { bg: "bg-slate-50", text: "text-slate-500", border: "border-slate-100" }
};

const TierIcon = ({ tier, className = "h-4 w-4" }) => {
  switch (tier) {
    case "Gold": return <Trophy className={`${className} text-yellow-500`} />;
    case "Silver": return <Medal className={`${className} text-slate-400`} />;
    case "Bronze": return <Award className={`${className} text-orange-500`} />;
    default: return <Users className={`${className} text-slate-400`} />;
  }
};

const TierBadge = ({ tier }) => {
  const style = TIER_STYLES[tier] || TIER_STYLES.Unranked;
  return (
    <Badge className={cn("gap-1", style.bg, style.text, "border", style.border)}>
      <TierIcon tier={tier} className="h-3 w-3" />
      {tier}
    </Badge>
  );
};

export default function Companies() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [tier, setTier] = useState(searchParams.get("tier") || "all");
  const [therapeuticArea, setTherapeuticArea] = useState(searchParams.get("therapeutic_area") || "all");
  const [taOptions, setTaOptions] = useState([]);
  const [stats, setStats] = useState({ total: 0, gold: 0, silver: 0, bronze: 0 });

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (tier && tier !== "all") params.append("tier", tier);
      if (search) params.append("search", search);
      if (therapeuticArea && therapeuticArea !== "all") params.append("therapeutic_area", therapeuticArea);

      const response = await axios.get(`${API}/companies?${params.toString()}`);
      setCompanies(response.data.companies || []);
      setStats(response.data.stats || { total: 0, gold: 0, silver: 0, bronze: 0 });
    } catch (error) {
      console.error("Fetch companies error:", error);
      toast.error("Failed to load companies");
    } finally {
      setLoading(false);
    }
  }, [tier, search, therapeuticArea]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  useEffect(() => {
    axios.get(`${API}/dashboard/therapeutic-areas-list`).then(res => {
      setTaOptions(res.data.therapeutic_areas || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    if (tier && tier !== "all") params.set("tier", tier);
    if (search) params.set("search", search);
    setSearchParams(params);
  }, [tier, search, setSearchParams]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
  };

  const handleTierChange = (value) => {
    setTier(value);
  };

  return (
    <div className="space-y-6" data-testid="companies-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1C2A25] font-['Outfit'] tracking-tight">Companies</h1>
          <p className="text-slate-500 mt-1">
            {stats.total} companies with leads
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm">
            <Badge className="bg-yellow-100 text-yellow-800">{stats.gold} Gold</Badge>
            <Badge className="bg-slate-100 text-slate-700">{stats.silver} Silver</Badge>
            <Badge className="bg-orange-100 text-orange-800">{stats.bronze} Bronze</Badge>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search companies..."
                value={search}
                onChange={handleSearch}
                className="pl-10"
                data-testid="company-search"
              />
            </div>
            <Select value={tier} onValueChange={handleTierChange}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="company-tier-filter">
                <SelectValue placeholder="All Tiers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tiers</SelectItem>
                <SelectItem value="Gold">Gold</SelectItem>
                <SelectItem value="Silver">Silver</SelectItem>
                <SelectItem value="Bronze">Bronze</SelectItem>
                <SelectItem value="Unranked">Unranked</SelectItem>
              </SelectContent>
            </Select>
            <Select value={therapeuticArea} onValueChange={setTherapeuticArea}>
              <SelectTrigger className="w-full sm:w-[200px]" data-testid="company-ta-filter">
                <SelectValue placeholder="All TAs" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Therapeutic Areas</SelectItem>
                {taOptions.map(ta => (
                  <SelectItem key={ta} value={ta}>{ta}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Companies List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-[#2E7AD1]" />
            </div>
          ) : companies.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-500">
              <Building2 className="h-12 w-12 mb-4 text-slate-300" />
              <p className="text-lg font-medium">No companies found</p>
              <p className="text-sm">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="overflow-auto max-h-[600px]">
            <Accordion type="single" collapsible className="w-full">
              {companies.map((company, idx) => (
                <AccordionItem key={company.name || idx} value={company.name || `company-${idx}`}>
                  <AccordionTrigger className="px-6 py-4 hover:bg-slate-50">
                    <div className="flex items-center justify-between w-full pr-4">
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-slate-100 rounded-lg">
                          <Building2 className="h-5 w-5 text-slate-600" />
                        </div>
                        <div className="text-left">
                          <p className="font-semibold text-slate-900">{company.name || "Unknown Company"}</p>
                          <p className="text-sm text-slate-500">{company.lead_count} leads</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <TierBadge tier={company.tier} />
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="px-6 pb-4">
                      <div className="bg-slate-50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <p className="text-sm font-medium text-slate-700">Contacts ({company.leads?.length || 0})</p>
                          <div className="flex gap-2">
                            <Link to={`/companies/${encodeURIComponent(company.name)}`}>
                              <Button variant="default" size="sm" className="bg-[#2E7AD1] hover:bg-[#2563EB]">
                                View Company <Building2 className="h-3 w-3 ml-1" />
                              </Button>
                            </Link>
                            <Link to={`/leads?search=${encodeURIComponent(company.name)}`}>
                              <Button variant="outline" size="sm">
                                All Leads <ExternalLink className="h-3 w-3 ml-1" />
                              </Button>
                            </Link>
                          </div>
                        </div>
                        <ScrollArea className="max-h-[300px]">
                          <div className="space-y-2">
                            {company.leads?.slice(0, 10).map((lead, lidx) => (
                              <Link 
                                key={lead.id || lidx}
                                to={`/leads/${lead.id}`}
                                className="block"
                              >
                                <div className="flex items-center justify-between p-3 bg-white rounded-lg border hover:border-slate-300 transition-colors">
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-slate-900 truncate">
                                      {lead.first_name || lead.contact || "N/A"} {lead.last_name || ""}
                                    </p>
                                    <p className="text-xs text-slate-500 truncate">{lead.role || "No role"}</p>
                                  </div>
                                  <div className="flex items-center gap-4 ml-4">
                                    {lead.email && (
                                      <div className="flex items-center gap-1 text-xs text-slate-500">
                                        <Mail className="h-3 w-3" />
                                        <span className="truncate max-w-[150px]">{lead.email}</span>
                                      </div>
                                    )}
                                    {lead.status && typeof lead.status === 'string' && lead.status.length > 0 && (
                                      <Badge variant="outline" className="text-xs">
                                        {lead.status.slice(0, 15)}
                                      </Badge>
                                    )}
                                    <ChevronRight className="h-4 w-4 text-slate-400" />
                                  </div>
                                </div>
                              </Link>
                            ))}
                            {company.leads?.length > 10 && (
                              <p className="text-center text-sm text-slate-500 py-2">
                                + {company.leads.length - 10} more leads
                              </p>
                            )}
                          </div>
                        </ScrollArea>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
